import { Button, Input, Skeleton } from '@h2hspark/ui';
import { QrCameraVideo, useQrCamera } from '@h2hspark/ui/camera';
import { ScanLine, X } from 'lucide-react';
import * as React from 'react';

import { useReturnFocusToOpener } from '../../member/use-overlay-focus-return';

import {
  CAMERA_STARTING_MESSAGE,
  MEMBER_TOKEN_PATTERN,
  NOT_A_CONNECT_CODE_MESSAGE,
  SCANNER_CAPTURE_LABEL,
  SCANNER_CLOSE_LABEL,
  SCANNER_HEADING,
  SCANNER_PASTE_LABEL,
  SCANNER_PASTE_PLACEHOLDER,
  SCANNER_PROMPT,
  SCAN_UNREACHABLE_MESSAGE,
  cameraMessage,
  connectTokenFromScan,
  type ScanStage,
} from './constants';

/**
 * The scanner (`MEM-028` AC-5, `MEM-029` AC-1, `MEM-031` AC-3).
 *
 * Transcribed from `Member App.dc.html`'s scanner overlay and `Web App.dc.html`'s `scanOpen`: a
 * full-bleed `#0d0716` panel with a radial `--purple-800` wash, "Scan to connect" and a close
 * control at the top, a framed viewport with four fuchsia corner brackets, the prompt beneath it,
 * and the capture action at the bottom.
 *
 * ## The camera
 *
 * The frame holds a `<video>` of the rear camera, and a frame is handed to a decoder four times a
 * second — the browser's own `BarcodeDetector` where there is one, and a pinned WebAssembly
 * ponyfill of the same interface where there is not. Both are `@h2hspark/ui/camera`'s, shared with
 * the check-in screen since `EVT-042` AC-6; its `qr-decoder.ts` carries which library, why that
 * one, and where its bytes land. What this file adds is what a decoded string means here.
 *
 * **The field stays.** It is not a placeholder that the camera replaces: a refused permission, a
 * device with no camera, a lens that will not focus in a dark room and a code on somebody's laptop
 * across the table are all real, and the token path is identical whichever fed it. Every camera
 * failure's copy ends by pointing at it.
 *
 * ## Nothing intervenes at the tap
 *
 * `MEM-029` AC-1. A decode calls `onSubmitCode` directly — no "is this them?" card, no confirmation
 * step. **Permission belongs to opening the scanner**, which the member does deliberately, so the
 * browser's prompt is raised here on open and never between two people mid-conversation.
 *
 * ## What is filtered, and why that is not "nothing happened"
 *
 * A room is full of QR codes that are not ours. `connectTokenFromScan` keeps only what is
 * token-shaped, so a menu never produces a scan request and never produces `MEM-031` AC-3's visible
 * failure for something the member did not aim at. Decoding *a* code that is not ours says so
 * instead, because a viewfinder that does nothing while somebody holds a phone at a poster is worse
 * than a sentence.
 *
 * ## The stream is stopped on every exit
 *
 * Closing, a successful scan, unmounting, and a navigation all release the camera, through the one
 * teardown `useQrCamera` runs for every way out. A phone whose light stays on after the sheet
 * closes is the kind of thing that gets an app deleted.
 */
export function ScannerDialog({
  stage,
  busy,
  onSubmitCode,
  onClose,
}: {
  readonly stage: ScanStage;
  readonly busy: boolean;
  readonly onSubmitCode: (token: string) => void;
  readonly onClose: () => void;
}): React.ReactElement | null {
  const [code, setCode] = React.useState('');
  const fieldRef = React.useRef<HTMLInputElement>(null);

  const open =
    stage.kind === 'scanning' || stage.kind === 'refused' || stage.kind === 'unreachable';
  /*
   * **This one is not a Radix dialog**, so nothing gives it focus return for free: it is a
   * `<div role="dialog">` holding a camera, and converting it is a change to a component that
   * owns a media stream. What it gets here is the half that was broken — the keyboard came out of
   * the scanner onto `<body>` — restored from its own unmount.
   *
   * What it still does **not** have is a focus trap, and that is a real gap rather than an
   * oversight deferred quietly: Tab from inside this panel reaches the page behind it. Recorded in
   * `docs/design/member-web-parity-2026-09.md` under (a)-25 with what converting it would cost.
   */
  const { restore } = useReturnFocusToOpener(open);
  React.useEffect(() => {
    if (!open) return undefined;
    return () => {
      restore();
    };
  }, [open, restore]);
  const [sawForeignCode, setSawForeignCode] = React.useState(false);
  React.useEffect(() => {
    if (!open) setSawForeignCode(false);
  }, [open]);
  const camera = useQrCamera({
    active: open,
    // While a scan is in flight, or one has just been refused, frames stop being submitted: the
    // member is looking at an answer, and a second request fired at the same code under their thumb
    // would replace it with an identical one.
    paused: busy || stage.kind !== 'scanning',
    onFrame: (payloads) => {
      for (const payload of payloads) {
        const token = connectTokenFromScan(payload, MEMBER_TOKEN_PATTERN);
        if (token !== null) {
          setSawForeignCode(false);
          // One tap, and nothing between this and the record (`MEM-029` AC-1).
          onSubmitCode(token);
          return;
        }
      }
      if (payloads.length > 0) setSawForeignCode(true);
    },
  });

  // Focus the field on open only when there is no picture to look at, so the keyboard does not
  // cover the viewfinder on a phone that is about to work.
  React.useEffect(() => {
    if (open && camera.state.kind !== 'live' && camera.state.kind !== 'starting') {
      fieldRef.current?.focus();
    }
  }, [open, camera.state.kind]);

  if (!open) return null;

  const submit = (): void => {
    const token = code.trim();
    if (token.length === 0 || busy) return;
    setCode('');
    onSubmitCode(token);
  };

  const cameraNotice = cameraMessage(camera.state);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={SCANNER_HEADING}
      className="fixed inset-0 z-50 flex w-full flex-col bg-[#0d0716] text-white"
    >
      <div className="flex flex-none items-center justify-between px-5 pb-3 pt-12">
        <span className="font-brand text-md font-bold">{SCANNER_HEADING}</span>
        <button
          type="button"
          onClick={onClose}
          aria-label={SCANNER_CLOSE_LABEL}
          className="inline-flex h-9 w-9 items-center justify-center rounded-pill bg-white/15 text-white"
        >
          <X className="h-5 w-5" aria-hidden />
        </button>
      </div>

      <div className="flex flex-1 flex-col items-center justify-center gap-6 px-6">
        {/* The viewport. The camera preview mounts here; the brackets are the prototype's. */}
        <div className="relative h-56 w-56 overflow-hidden rounded-2xl bg-white/10">
          <QrCameraVideo
            ref={camera.videoRef}
            className={
              camera.state.kind === 'live'
                ? 'absolute inset-0 h-full w-full object-cover'
                : 'hidden'
            }
          />
          {camera.state.kind === 'live' ? null : (
            <div className="absolute inset-0 flex items-center justify-center text-white/30">
              <ScanLine className="h-20 w-20" aria-hidden />
            </div>
          )}
          <span className="absolute left-3.5 top-3.5 h-7 w-7 rounded-tl-xl border-l-[3px] border-t-[3px] border-fuchsia-300" />
          <span className="absolute right-3.5 top-3.5 h-7 w-7 rounded-tr-xl border-r-[3px] border-t-[3px] border-fuchsia-300" />
          <span className="absolute bottom-3.5 left-3.5 h-7 w-7 rounded-bl-xl border-b-[3px] border-l-[3px] border-fuchsia-300" />
          <span className="absolute bottom-3.5 right-3.5 h-7 w-7 rounded-br-xl border-b-[3px] border-r-[3px] border-fuchsia-300" />
        </div>

        <p className="max-w-[16rem] text-center text-sm text-white/75">{SCANNER_PROMPT}</p>

        {/*
          The camera's own state. `starting` says so and nothing more; a refusal, a missing camera
          and a breakage each say what happened and point at the field below.
        */}
        {cameraNotice === null ? null : (
          <p
            role={cameraNotice === CAMERA_STARTING_MESSAGE ? undefined : 'status'}
            data-testid="camera-notice"
            data-camera-state={camera.state.kind}
            className="max-w-[19rem] text-center text-sm text-white/80"
          >
            {cameraNotice}
          </p>
        )}

        {/* A QR that decoded and is not a connect code. Nothing was sent and nothing refused. */}
        {sawForeignCode && camera.state.kind === 'live' ? (
          <p role="status" className="max-w-[19rem] text-center text-sm text-white">
            {NOT_A_CONNECT_CODE_MESSAGE}
          </p>
        ) : null}

        {/*
          The token path, typed or pasted — the fallback whenever the lens is not the way in. The
          shape of what arrives at `onSubmitCode` is the same either way.
        */}
        <Input
          ref={fieldRef}
          value={code}
          aria-label={SCANNER_PASTE_LABEL}
          placeholder={SCANNER_PASTE_PLACEHOLDER}
          onChange={(changed) => {
            setCode(changed.target.value);
          }}
          onKeyDown={(pressed) => {
            if (pressed.key === 'Enter') submit();
          }}
          className="w-full max-w-xs border-white/20 bg-white/10 text-white placeholder:text-white/40"
        />

        {/*
          `MEM-031` AC-3: a scan that did not reach us says so, here, and shows no success state.
          There is no branch on this screen that turns either of these into a logged connection.
        */}
        {stage.kind === 'refused' ? (
          <p role="status" className="max-w-[18rem] text-center text-sm text-white">
            {stage.message}
          </p>
        ) : null}
        {stage.kind === 'unreachable' ? (
          <p role="status" className="max-w-[18rem] text-center text-sm text-white">
            {SCAN_UNREACHABLE_MESSAGE}
          </p>
        ) : null}
      </div>

      <div className="flex-none px-5 pb-8 pt-4">
        {busy ? (
          <Skeleton className="h-12 w-full rounded-pill" />
        ) : (
          <Button className="w-full" size="lg" onClick={submit} disabled={code.trim().length === 0}>
            <ScanLine className="mr-2 h-5 w-5" aria-hidden />
            {SCANNER_CAPTURE_LABEL}
          </Button>
        )}
      </div>
    </div>
  );
}
